/**
 * Ссылки на поставщика в расчёте (WS 15587410, п.7): Alibaba, 1688, биржа — любой
 * адрес. Логика отдельно от виджета, чтобы проверка адреса тестировалась без DOM.
 */

export interface SupplierLink {
  url: string;
  /** Короткая пометка человека: цена, MOQ, «образец заказан». */
  note?: string;
}

export type SupplierSource = 'alibaba' | '1688' | 'aliexpress' | 'made_in_china' | 'other';

/** Тот же предел, что в схеме бэкенда (`SupplierLink`, `MAX_SUPPLIERS`). */
export const MAX_SUPPLIERS = 10;
export const MAX_URL_LENGTH = 2048;
export const MAX_NOTE_LENGTH = 200;

/**
 * Адрес, который можно сохранить и открыть, или `null`.
 *
 * Без схемы дописываем `https://` — так ссылку обычно и вставляют, «1688.com/…».
 * Пропускаем только http(s): ссылка открывается кликом, и `javascript:` здесь был
 * бы XSS для каждого, кто откроет расчёт по публичной ссылке.
 */
export function normalizeSupplierUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed || trimmed.length > MAX_URL_LENGTH) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
  let parsed: URL;
  try {
    parsed = new URL(withScheme);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
  if (!parsed.hostname.includes('.')) return null;
  return parsed.href;
}

const SOURCES: { source: Exclude<SupplierSource, 'other'>; hosts: string[] }[] = [
  { source: '1688', hosts: ['1688.com'] },
  { source: 'alibaba', hosts: ['alibaba.com'] },
  { source: 'aliexpress', hosts: ['aliexpress.com', 'aliexpress.ru', 'aliexpress.us'] },
  { source: 'made_in_china', hosts: ['made-in-china.com'] },
];

/** Площадка по домену — для метки рядом со ссылкой. */
export function supplierSource(url: string): SupplierSource {
  let host: string;
  try {
    host = new URL(url).hostname.toLowerCase();
  } catch {
    return 'other';
  }
  const hit = SOURCES.find(({ hosts }) => hosts.some((h) => host === h || host.endsWith(`.${h}`)));
  return hit?.source ?? 'other';
}

/** Хост без `www.` — коротко показать, куда ведёт ссылка. */
export function displayHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

export type AddResult = { ok: true; links: SupplierLink[] } | { ok: false; reason: 'invalid' | 'duplicate' | 'limit' };

export function addSupplierLink(links: SupplierLink[], rawUrl: string, rawNote: string): AddResult {
  if (links.length >= MAX_SUPPLIERS) return { ok: false, reason: 'limit' };
  const url = normalizeSupplierUrl(rawUrl);
  if (!url) return { ok: false, reason: 'invalid' };
  if (links.some((l) => l.url === url)) return { ok: false, reason: 'duplicate' };
  const note = rawNote.trim().slice(0, MAX_NOTE_LENGTH);
  return { ok: true, links: [...links, note ? { url, note } : { url }] };
}

/**
 * Ссылки из сохранённого расчёта. Запись могла прийти откуда угодно (MCP, старые
 * версии), поэтому каждый адрес проходит ту же проверку, что и ввод.
 */
export function parseSupplierLinks(raw: unknown): SupplierLink[] {
  if (!Array.isArray(raw)) return [];
  const out: SupplierLink[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const { url, note } = item as { url?: unknown; note?: unknown };
    const safe = typeof url === 'string' ? normalizeSupplierUrl(url) : null;
    if (!safe || out.some((l) => l.url === safe)) continue;
    const text = typeof note === 'string' ? note.trim().slice(0, MAX_NOTE_LENGTH) : '';
    out.push(text ? { url: safe, note: text } : { url: safe });
    if (out.length === MAX_SUPPLIERS) break;
  }
  return out;
}
